import React, { useCallback } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import AccuracyRing from "./AccuracyRing";
import DashboardSectionEmptyState from "./DashboardSectionEmptyState";
import changeCourseChevron from "../../../utilities/images/study_dashboard/change_course_chevron.svg";
import recentTestsEmptyIcon from "../../../utilities/images/study_dashboard/empty_states/recent_tests.svg";
import { navigateFromPracticeOrigin } from "../../../custom_utilities/dashboardBannerNavigation";
import {
  setPaperId,
  setTestDetails,
  setTestId,
  setTestType,
  setSelectedSetId,
} from "../../../redux/exam/examSlice";
import {
  getRecentTestAnchorId,
  RECENT_TESTS_SECTION_ID,
  saveStudyDashboardScrollState,
} from "../utils/studyDashboardScrollRestore";
import "./RecentTests.css";

const SKELETON_ROW_COUNT = 2;

const RecentTestsSkeleton = () => (
  <div className="recent-tests__list" aria-hidden="true">
    {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
      <article key={index} className="recent-tests__item recent-tests__item--skeleton">
        <span className="recent-tests__skeleton recent-tests__skeleton--ring" />
        <div className="recent-tests__content">
          <span className="recent-tests__skeleton recent-tests__skeleton--title" />
          <span className="recent-tests__skeleton recent-tests__skeleton--score" />
        </div>
        <span className="recent-tests__skeleton recent-tests__skeleton--date" />
      </article>
    ))}
  </div>
);

const RecentTests = ({ tests = [], isLoading = false }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const showEmpty = !isLoading && !tests.length;

  const handleAnalyticsClick = useCallback(
    (test) => {
      const paperId = test?.paperId ?? test?.id;
      const paperType = test?.paperType;
      if (paperId == null || paperType == null) return;

      const anchorId = getRecentTestAnchorId(paperId);
      saveStudyDashboardScrollState({ anchorId });

      const plannerTestId = test?.plannerTestId;
      const isAitsStyle = Boolean(test?.isAitsStyle) || Number(paperType) === 3;
      const payload = {
        paperId,
        paperType,
        testId: plannerTestId || paperId,
        setId: null,
        planner_test_id: plannerTestId || (isAitsStyle ? paperId : undefined),
        practiceMode: "test",
        gamificationOn: false,
        source: "dashboard",
        returnTo: "/dashboard",
        test_name: test?.title || "",
      };

      dispatch(setTestId(payload.testId));
      dispatch(setTestType({ id: paperType }));
      dispatch(setSelectedSetId(null));
      dispatch(setPaperId(payload.paperId));
      dispatch(setTestDetails(payload));

      navigateFromPracticeOrigin(history, {
        pathname: "/analytics",
        state: {
          paper_type: paperType,
          paper_id: paperId,
          returnTo: "/dashboard",
          ...(payload.planner_test_id
            ? { planner_test_id: payload.planner_test_id }
            : {}),
        },
      });
    },
    [dispatch, history]
  );

  return (
    <section
      id={RECENT_TESTS_SECTION_ID}
      className={`recent-tests${showEmpty ? " recent-tests--empty" : ""}`}
      aria-labelledby="recent-tests-title"
      aria-busy={isLoading || undefined}
    >
      <div className="recent-tests__header">
        <h2 id="recent-tests-title" className="recent-tests__title">
          Recent Tests
        </h2>
      </div>

      {isLoading ? (
        <RecentTestsSkeleton />
      ) : showEmpty ? (
        <DashboardSectionEmptyState
          icon={recentTestsEmptyIcon}
          title="No Tests Taken Yet"
          description="Attempt a test to see your recent performance here"
        />
      ) : (
        <div className="recent-tests__list">
          {tests.map((test) => {
            const canOpenAnalytics =
              test.paperId != null && test.paperType != null;
            const anchorId = getRecentTestAnchorId(test.paperId ?? test.id);

            return (
              <article
                key={test.id}
                id={anchorId}
                className="recent-tests__item"
              >
                {test.percent != null ? (
                  <AccuracyRing percent={test.percent} />
                ) : (
                  <div className="recent-tests__type-badge" aria-hidden="true">
                    {(test.scoreLabel || "TEST").slice(0, 3)}
                  </div>
                )}
                <div className="recent-tests__content">
                  <h3 className="recent-tests__name">{test.title}</h3>
                  {test.scoreLabel ? (
                    <p className="recent-tests__score">{test.scoreLabel}</p>
                  ) : null}
                </div>
                <div className="recent-tests__meta">
                  {test.dateLabel ? (
                    <span className="recent-tests__date">{test.dateLabel}</span>
                  ) : null}
                  {canOpenAnalytics ? (
                    <button
                      type="button"
                      className="recent-tests__analytics-link"
                      onClick={() => handleAnalyticsClick(test)}
                    >
                      Analytics
                      <img src={changeCourseChevron} alt="" aria-hidden="true" />
                    </button>
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default RecentTests;
